import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowUp, Loader2, Sparkles, Square } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { TOPIC_LABELS, type AskStage, type ChatMessage } from "@gb/contracts";
import { useProjectContext } from "@/app/project-context";
import { useBrainSummary } from "@/features/brain/brain-api";
import { BrainTabs } from "@/features/brain/brain-tabs";
import { useDocuments } from "@/features/project/documents-api";
import { HazardStripe } from "@/components/gb/hazard-stripe";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AnswerBody } from "./answer-body";
import { ThreadDebug } from "@/features/debug/debug-panels";
import { AcceptDecision } from "./accept-decision";
import { AnswerLines } from "./answer-lines";
import { AnswerSteps, type StepFocus } from "./answer-steps";
import { requestCancel, useAnswerSteps } from "./answer-steps-api";
import { useAwaitingAnswer } from "./answer-wait";
import { useChatThread, useCreateThread } from "./chat-api";
import { ConfidenceBand } from "./confidence-band";
import { MarkWrong } from "./mark-wrong";
import { AnswersFrom, DocumentsAvailable, HowTheBrainLearns } from "./ask-onboarding";
import { askedAtLabel } from "./question-history";
import { SourceCard, sourceAnchorId } from "./source-card";
import { SourceGroupBlock, groupSources } from "./source-groups";
import { useAskStream } from "./ask-stream";

/**
 * Asking the Site Brain **one** question.
 *
 * This screen is not a conversation. A question is asked, answered once, and
 * kept; asking again starts a new record rather than adding a turn. The
 * threaded chat this was built as is shelved, not deleted — see
 * `product/0005-a-question-is-answered-once.md` — which is why the stores, the
 * contract and this folder are all still called `chat`. The tables genuinely
 * are threads; there is exactly one question in each of them for now.
 *
 * The ask box sits at the top rather than under the answer, because it is the
 * subject of the screen and not a reply box. With nothing asked it sits in the
 * middle of the page, and once a question is asked it empties, ready for the
 * next one: the question being answered is the panel's heading, not the box's
 * text (the owner, 2026-09-27, `product/0013`).
 *
 * **One column** (2026-09-27, `product/0013`). From the top: the question and
 * when it was asked, the confidence band, the steps, the answer, one row of
 * what to do with it, then the sources it cites, full width. The sources used
 * to be a rail beside the answer. They are still the difference between an
 * answer and a claim, and each appears as the answer cites it, so a citation
 * resolves to a card the moment it is written.
 *
 * What has been asked before is on the History tab, not on this one.
 */

// The answering agent's stages (2026-09-26, Phase 5). The old path's
// `selecting` and `reading` went with it (slice X, 2026-09-27).
const STAGE_LABELS: Record<AskStage, string> = {
  thinking: "Thinking…",
  working: "Working through the files…",
  answering: "Writing the answer…",
  checking: "Checking the answer…",
};

export function ChatScreen() {
  const { questionId } = useParams();
  const { project, projectId } = useProjectContext();
  const navigate = useNavigate();

  const { data, isPending, isError } = useChatThread(projectId, questionId);
  // Held above the Brain's tabs, not by this screen, so it outlives a visit
  // to the History tab (`ask-stream.tsx`).
  const stream = useAskStream();
  const createThread = useCreateThread(projectId);
  // Only the empty state reads these. The documents query is shared with the
  // Sources tab, and the summary is the Sources tab's own counts and its
  // categories.
  const documents = useDocuments(questionId ? undefined : projectId);
  const summary = useBrainSummary(questionId ? undefined : projectId);

  const [draft, setDraft] = useState("");
  const [highlighted, setHighlighted] = useState<number | null>(null);
  // "Found in step N" on a source card: the step list opens at it.
  const [stepFocus, setStepFocus] = useState<StepFocus | null>(null);
  // The words just asked, for the heading, until the stored question is read
  // back. The thread is created empty and the ask writes the question, so the
  // first read of a new question can come back without it.
  const [justAsked, setJustAsked] = useState<{ id: string; text: string } | null>(null);

  // A question handed over by whoever navigated here — the hero on Today, and
  // this screen itself when a new question is asked from an old one. Asked once
  // per record, then cleared from history so a reload or a Back does not re-run
  // it.
  //
  // Keyed on the record it was asked for, **not** a bare boolean. Moving from
  // one question to another changes only the route param, so this component is
  // not remounted and a `useRef(false)` stays `true` — the second question
  // would post nothing and sit on a permanently empty answer.
  const location = useLocation();
  const handoff = (location.state as { ask?: string } | null)?.ask;
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!handoff || !questionId || askedFor.current === questionId) return;
    askedFor.current = questionId;
    setJustAsked({ id: questionId, text: handoff });
    void stream.ask(questionId, handoff);
    void navigate(location.pathname, { replace: true, state: null });
  }, [handoff, questionId, stream, navigate, location.pathname]);

  // **The box is not filled from the question shown** (2026-09-27). It used to
  // show the question it was displaying, as the first design did, so asking
  // left the words in the box and opening an old question put them back. The
  // question is the panel's heading now, and the box is for the next one.
  const stored = data?.messages ?? [];
  const askedMessage = stored.find((message) => message.role === "user");
  const questionText =
    askedMessage?.content ?? (justAsked && justAsked.id === questionId ? justAsked.text : null);
  const askedAt = askedMessage?.createdAt ?? data?.thread.createdAt ?? null;

  /**
   * Ask something new.
   *
   * Always a fresh record, from either entry point: the server refuses a second
   * ask against an answered one with a 409, so reusing this record would fail
   * on the wire rather than quietly appending.
   */
  const startQuestion = async (question: string) => {
    // A second question stops the first (`product/0005` §2.6), also when the
    // first runs with no stream in this tab (2026-09-27). The hook stops only
    // the question it streams.
    if (questionId && running && !live) void requestCancel(projectId, questionId);
    const thread = await createThread.mutateAsync(undefined);
    // Emptied once the new record exists, so a failure leaves the words where
    // the reader typed them.
    setDraft("");
    stream.reset();
    void navigate(`/projects/${projectId}/brain/ask/${thread.id}`, {
      state: { ask: question },
    });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const question = draft.trim();
    // One stream at a time — the hook aborts any in flight when a new ask
    // starts, so letting this through would kill an answer the reader is still
    // waiting on somewhere else.
    if (!question || stream.pending) return;
    void startQuestion(question);
  };

  /** The question shown, asked again as a new record. The box no longer holds its words. */
  const askAgain = () => {
    if (!questionText || stream.pending) return;
    void startQuestion(questionText);
  };

  const showCitation = (n: number) => {
    setHighlighted(n);
    const card = document.getElementById(sourceAnchorId(n));
    // **Unfold the group first.** Sources are grouped by kind and a group of
    // more than four starts closed, so a citation into `Crew · 26` used to
    // scroll to a card inside a collapsed `<details>` — the rail moved and
    // showed nothing, which is worse than the flat list this replaced.
    card?.closest("details")?.setAttribute("open", "");
    card?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // The stored answer wins as soon as it arrives: it has had its dead citation
  // markers stripped, so it is the corrected version of what streamed.
  const answer = stored.find((message) => message.role === "assistant");
  // Only this question's own stream. An answer still being written for another
  // question keeps running in the background — see the banner below — but it
  // does not render here, under a title it does not belong to.
  const live = stream.askedFor === questionId;
  const streaming = live && (stream.pending || Boolean(stream.answer));
  // Somewhere else is still being answered. The stream survives moving to
  // another question, and a visit to the History tab, because it is held
  // above the Brain's tabs (`ask-stream.tsx`), so the way back is a link
  // rather than a rerun.
  const answeringElsewhere =
    stream.pending && stream.askedFor !== null && stream.askedFor !== questionId;
  // **`live` here too.** The stream's sources arrive before the text that
  // cites them, so they are the right thing to show *for the question being
  // answered* — and the wrong thing for any other. Without the guard, opening a
  // different question while one was streaming borrowed the streaming
  // question's sources into this one's list and into its confidence band's
  // "N sources" line: two unrelated questions both reading "18 sources · last
  // confirmed 5 Aug".
  const sources = live && stream.sources.length > 0 ? stream.sources : (answer?.sources ?? []);

  // This question's own error. The hook keeps it after the stream ends, and
  // it belongs under this question only, not under the next one opened.
  const error = stream.errorFor === questionId ? stream.error : null;
  // The server said the answer will not come (2026-09-27).
  const failed = error !== null && stream.failed;
  const awaiting = useAwaitingAnswer(data);
  // Running: this tab's stream is open, or, with no stream here, the question
  // has no answer yet and can still get one (the server carries on after a
  // reload). It cannot when an error said so, or when it was asked more than
  // 25 minutes ago (2026-09-27): some failures store no answer row, and such
  // a question used to read as running, with both polls on, for as long as
  // the tab stayed open.
  const running = live ? stream.pending && !answer : !answer && !failed && awaiting;
  // **Stop whenever this question is being answered** (2026-09-27), not only
  // while this tab's stream is open. The answer runs on when the stream goes:
  // a tab switch unmounts this screen, and a reload or another tab never had
  // it. Stop is stored on the question, so any screen that shows it can stop
  // it. Once asked, the button waits for the stopped answer to be stored.
  const answering = streaming || running;
  const [stopAsked, setStopAsked] = useState<string | null>(null);
  // The question whose Stop was not stored (2026-09-27). With no stream here
  // there is nothing to close, so that stop stopped nothing: the screen says
  // so and offers Stop again, as the backoffice does.
  const [stopFailed, setStopFailed] = useState<string | null>(null);
  const stopping = !live && running && stopAsked === questionId;
  const stop = () => {
    if (live) {
      stream.cancel();
      return;
    }
    if (!questionId) return;
    const asked = questionId;
    setStopAsked(asked);
    setStopFailed(null);
    void requestCancel(projectId, asked).then((outcome) => {
      if (outcome !== "failed") return;
      setStopAsked((current) => (current === asked ? null : current));
      setStopFailed(asked);
    });
  };

  // **How the answer was made** (2026-09-26, Phase 5). While this tab streams
  // the answer, the steps come with it. Otherwise they are read from the
  // trail: polled while the question has no answer (a reload mid-answer), and
  // read once for an answer that has a trail. An answer made before the agent,
  // and a scripted demo answer, have none, and get no block.
  const storedSteps = useAnswerSteps(projectId, questionId, {
    // Not before the thread is read: until then an answered question looks
    // unanswered.
    enabled: Boolean(data) && ((running && !live) || Boolean(answer?.trail)),
    answered: Boolean(answer),
  });
  const steps = live && stream.steps.length > 0 ? stream.steps : (storedSteps.data ?? []);
  const trail = answer?.trail ?? (live ? stream.trail : null) ?? null;
  const showSteps = !isPending && (running || trail !== null || steps.length > 0);
  const coverage = answer?.coverage ?? (live ? stream.coverage : null) ?? null;
  const unfinished = answer?.unfinished ?? (live ? stream.unfinished : null) ?? null;
  const findStep = (seq: number) => setStepFocus({ seq, nonce: Date.now() });

  // Whether this question has already been elevated, from its own record. Not
  // read out of the History tab's list: that list is paged, so a question
  // opened from page three is simply absent from the page in hand — and absent
  // would read as "not yet elevated" and offer to file it twice.
  const decision = data?.decisionId ?? null;

  // The empty state's one line, once both counts are known. A project with no
  // files at all gets the setup cards under it as well.
  const noFiles = documents.data !== undefined && documents.data.length === 0;
  const answersFrom =
    summary.data && documents.data
      ? {
          files: summary.data.bySourceType["project-doc"] ?? 0,
          filesHeld: documents.data.length,
          manualSections: summary.data.bySourceType.manual ?? 0,
          notes: summary.data.bySourceType.upload ?? 0,
          decisions: summary.data.bySourceType.decision ?? 0,
        }
      : null;

  if (isError) {
    return (
      <div className="mx-auto max-w-[1180px] px-[30px] py-16">
        <p className="text-[0.92rem] font-semibold text-danger">
          That question could not be opened.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-4"
          onClick={() => void navigate(`/projects/${projectId}/brain/ask`)}
        >
          Ask a new one
        </Button>
      </div>
    );
  }

  const askBox = (
    <form className="relative" onSubmit={submit}>
      <span className="pointer-events-none absolute top-1/2 left-[18px] flex -translate-y-1/2 text-brand">
        <Sparkles size={18} />
      </span>
      <input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        // The design offers "across email, WhatsApp, docs & manuals". Two of
        // those are not ingested by anything, and a placeholder is a promise
        // about what the box can answer from — so it names what is actually
        // in the corpus. It goes back the moment those connectors land.
        placeholder={`Ask anything about ${project.name} — across documents, manuals & the journal`}
        aria-label={`Ask about ${project.name}`}
        className={cn(
          "h-[58px] w-full rounded-card border-[2px] border-ink bg-surface",
          "pr-[64px] pl-[48px] text-[1rem] text-ink outline-none",
          "placeholder:text-muted focus:border-brand",
        )}
      />
      <button
        // Stop only stops *this* question. While another one is still being
        // answered the control is disabled rather than repurposed: the hook
        // runs one stream at a time, so a second ask would silently abort the
        // first — and a button reading "Stop answering" on a page that is not
        // answering anything would cancel something off screen. A question
        // shown here that runs with no stream in this tab keeps its Stop:
        // that Stop is stored on it alone (`stop`).
        type={answering ? "button" : "submit"}
        onClick={answering ? stop : undefined}
        disabled={(answeringElsewhere && !answering) || stopping}
        aria-label={
          stopping
            ? "Stopping"
            : answering
              ? "Stop answering"
              : answeringElsewhere
                ? "Another question is still being answered"
                : "Ask"
        }
        className={cn(
          "absolute top-1/2 right-[8px] flex size-[42px] -translate-y-1/2 cursor-pointer",
          "items-center justify-center rounded-card border-none bg-brand text-white",
          "transition-colors hover:bg-brand-hover",
          "disabled:cursor-not-allowed disabled:opacity-40",
        )}
      >
        {/* The design puts a microphone here. The Journal composer dictates
            (`product/0014`); this box does not yet — it would use the
            `question` purpose, as the phone does. Until then, a button that
            looks like it listens and does not is worse than one that sends. */}
        {answering ? <Square size={15} /> : <ArrowUp size={18} />}
      </button>
    </form>
  );

  return (
    // **No scroll container here.** `architecture/0001`: the shell scrolls in
    // exactly one place, the content column, and the page never does. This
    // screen had its own `h-full overflow-y-auto`, so with the answer panel
    // scrolling inside it there were three nested scroll regions — the
    // wheel did something different depending on where the pointer was, and
    // the tabs and ask box scrolled away under a panel that was scrolling too.
    // The panel has no height of its own either (2026-09-27): it grows with
    // the steps and the answer, and the content column scrolls.
    //
    // With nothing asked the screen is a column at least as tall as the
    // content column, so the box can sit in the middle of what is left under
    // the tabs (below).
    <div
      className={cn(
        "mx-auto max-w-[1180px] px-[30px] pt-4 pb-12",
        !questionId && "flex min-h-full flex-col",
      )}
    >
      {/* A block of its own, so the tab bar keeps its own width when the
          screen is a flex column: a flex column stretches its items, and the
          tab bar is `inline-flex`. */}
      <div>
        {/* No notices here (the product owner, 2026-09-27): the import to
            review and the files the check asks about are the Sources tab's,
            where the files are. Ask is the question and its answer. */}
        <BrainTabs />

        {answeringElsewhere && (
          <Link
            to={`/projects/${projectId}/brain/ask/${stream.askedFor}`}
            className={cn(
              "mb-3 flex items-center gap-2 rounded-card border-[1.5px] border-brand/40 bg-brand-tint-3",
              "px-4 py-2.5 text-[0.86rem] font-semibold text-brand-on-tint",
              "transition-colors hover:bg-brand-tint-2",
            )}
          >
            <Loader2 size={15} className="animate-spin" aria-hidden />
            Still answering your other question — go back to it
          </Link>
        )}
      </div>

      {!questionId ? (
        // **Nothing asked: the box in the middle of the page, one line under
        // it** (the owner, 2026-09-27, `product/0013`). No answer panel and no
        // source list — an empty bordered box with "Sources — none yet" is
        // furniture. The setup cards only when the project has no files; with
        // files, the line says what the Brain answers from.
        //
        // **The middle of what is left under the tabs**, not of a fixed 45vh
        // block, which put the box in the upper third of a tall window. With
        // the setup cards under it the box does not take the rest of the
        // height: the cards would be pushed to the foot of the window.
        <>
          <div
            className={cn(
              "flex flex-col justify-center",
              noFiles ? "py-10" : "flex-1 py-8",
            )}
          >
            <div className="mx-auto w-full max-w-[760px]">
              {askBox}
              <AnswersFrom projectId={projectId} counts={answersFrom} />
            </div>
          </div>
          {noFiles && (
            <div className="flex flex-col gap-4">
              <HowTheBrainLearns projectId={projectId} />
              <DocumentsAvailable projectId={projectId} categories={summary.data?.categories} />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-4">{askBox}</div>

          {/* **One panel holding the question, the answer and everything that
              stands behind it**, read top to bottom (2026-09-27,
              `product/0013`). The sources are part of the answer — the
              citations point into them — so they stay on the same bordered
              surface, divided rather than detached, now under the answer
              rather than in a rail beside it.

              **No fixed height.** The panel used to be 62vh, clamped to
              420–680px, and scrolled inside itself, so an answer being written
              did not push the question history below it down the page
              (`product/0005` §2.4). The history has its own tab now, nothing
              sits below the panel, and the fixed box gave each section a
              slot of a slot. The panel grows with the steps and the answer,
              and the page scrolls. */}
          <section
            aria-labelledby={questionText ? "asked-question" : undefined}
            className="rounded-card-lg border-[1.5px] border-border bg-surface"
          >
            {/* The question is the heading: the box above is for the next
                one. */}
            {questionText && (
              <header className="px-6 pt-5 pb-3">
                <h2
                  id="asked-question"
                  className="text-[1.2rem] leading-snug font-semibold break-words text-ink"
                >
                  {questionText}
                </h2>
                {askedAt && (
                  <p className="mt-1 text-[0.8rem] text-muted">
                    <time dateTime={askedAt}>{askedAtLabel(askedAt)}</time>
                  </p>
                )}
              </header>
            )}

            <ConfidenceBand
              confidence={answer?.confidence ?? stream.confidence}
              note={answer?.confidenceNote ?? stream.confidenceNote}
              sources={sources}
              answer={answer?.content ?? (live ? stream.answer : undefined)}
            />

            <div className="px-6 py-5">
              {isPending ? (
                <div className="flex items-center gap-2 py-10 text-muted" role="status">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-[0.9rem]">Opening the question…</span>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {/* The steps, under the band and above the answer: one line
                      that opens to a sentence per step (§3 of the Phase 5
                      design). It holds the stage line too, and is the one part
                      of the panel that announces itself. Keyed by question, so
                      each question's list starts open or folded on its own
                      (`answer-steps.tsx`). */}
                  {showSteps && (
                    <AnswerSteps
                      key={questionId}
                      projectId={projectId}
                      steps={steps}
                      trail={trail}
                      running={running}
                      stage={live ? stream.stage : null}
                      stageLabel={live && stream.stage ? STAGE_LABELS[stream.stage] : null}
                      failed={failed}
                      sources={sources}
                      onCite={showCitation}
                      focus={stepFocus}
                    />
                  )}

                  {/* **Asked, not yet answered, and not streaming here.**
                      Questions appear in the history the moment they are asked,
                      so this is reachable — open one mid-answer, or reload while
                      it is being written and the buffer is gone from this tab
                      while the server carries on. Rendering nothing left an
                      empty panel that reads as a hang. The steps above say what
                      it is doing, so this line says only that it will come. */}
                  {running && !streaming && (
                    <p className="flex items-center gap-2 py-6 text-[0.9rem] text-muted">
                      <Loader2 size={15} className="animate-spin" aria-hidden />
                      {stopping
                        ? "Stopping — this will fill in on its own."
                        : "Still being answered — this will fill in on its own."}
                    </p>
                  )}
                  {running && !live && stopFailed === questionId && (
                    <p role="alert" className="text-[0.85rem] font-semibold text-danger">
                      Stop was not stored. The answer is still running.
                    </p>
                  )}

                  {/* **No answer, and none is coming** (2026-09-27): asked more
                      than 25 minutes ago with nothing stored. "Ask it again"
                      asks the same words as a new question: they are in the
                      heading, not in the box. A failure this tab saw says it in
                      its own words below instead. */}
                  {!answer && !live && !running && !failed && (
                    <div className="flex flex-wrap items-center gap-3 py-6">
                      <p className="text-[0.9rem] text-muted">
                        No answer on record. Asking it again files a new question.
                      </p>
                      {questionText && (
                        <Button type="button" variant="outline" size="sm" disabled={stream.pending} onClick={askAgain}>
                          Ask it again
                        </Button>
                      )}
                    </div>
                  )}

                  {answer && <Answer message={answer} onCite={showCitation} />}
                  {answer && <AnswerLines coverage={coverage} unfinished={unfinished} />}

                  {/* **Never both.** The stored answer is the corrected one —
                      dead citation markers are stripped before the row is
                      written — so once it exists it wins. Since `useChatThread`
                      started polling while a question is unanswered, the row can
                      land a beat before this tab processes `done`, and without
                      this guard the answer rendered twice. */}
                  {streaming && !answer && (
                    <>
                      {/* The review's topic arrives with `done`, a beat before
                          the stored answer is read back. */}
                      {stream.topic && (
                        <span className="inline-block font-display text-[0.62rem] font-semibold tracking-[0.08em] text-muted uppercase">
                          {TOPIC_LABELS[stream.topic]}
                        </span>
                      )}
                      {stream.answer && (
                        <AnswerBody
                          text={stream.answer}
                          sources={stream.sources}
                          onCite={showCitation}
                        />
                      )}
                      {(stream.coverage || stream.unfinished) && (
                        <AnswerLines coverage={stream.coverage} unfinished={stream.unfinished} />
                      )}
                    </>
                  )}

                  {error && (
                    <div className="flex flex-col items-start gap-2">
                      <p role="alert" className="text-[0.85rem] font-semibold text-danger">
                        {error}
                      </p>
                      {/* Only when the server said no answer will come. A
                          dropped connection is not that: the answer runs on at
                          the server, and the line above says it will fill in. */}
                      {failed && questionText && (
                        <Button type="button" variant="outline" size="sm" disabled={stream.pending} onClick={askAgain}>
                          Ask it again
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* **One row of what to do with the answer**, under it: accept it
                as a decision, accept it modified, or mark it as wrong (the
                owner, 2026-09-27). Accepting used to head the source rail. Only
                once there is an answer — the buttons are meaningless while it
                is still being written, and an answer that failed has nothing
                to file. "Mark as wrong" sits at the far end: it is the quiet
                one, and apart from Accept it is not pressed by mistake. */}
            {answer && questionId && (
              <div
                role="group"
                aria-label="What to do with this answer"
                className="flex flex-wrap items-start gap-x-3 gap-y-2 border-t border-border px-6 py-4"
              >
                <AcceptDecision
                  projectId={projectId}
                  questionId={questionId}
                  decided={Boolean(decision)}
                />
                <div className="ml-auto">
                  <MarkWrong
                    projectId={projectId}
                    threadId={questionId}
                    messageId={answer.id}
                    markedWrong={Boolean(answer.markedWrong)}
                  />
                </div>
              </div>
            )}

            {/* **The sources, full width, under the answer**, as cards two or
                three across (2026-09-27, `product/0013`). Only once one is
                cited: each arrives just before the text that cites it, from
                the agent and from a scripted demo answer alike
                (`ask-runner.service.ts`), and a "Sources — none yet" while
                nothing is cited reads as a list still loading. An answer that cites nothing has no list at all
                (R84, 2026-09-26); what it read is in the coverage line under
                the answer instead. */}
            {sources.length > 0 && (
              <section
                aria-labelledby="answer-sources-heading"
                className="border-t border-border px-6 py-5"
              >
                <h3
                  id="answer-sources-heading"
                  className="mb-2 font-display text-[0.66rem] font-semibold tracking-[0.1em] text-muted uppercase"
                >
                  Sources
                </h3>
                {/* Grouped by kind. A flat list is unreadable the moment an
                    answer cites crew — twenty-six near-identical cards with no
                    seam where the site notes end. See `source-groups.tsx`. */}
                <div className="flex flex-col">
                  {groupSources(sources).map((group) => (
                    <SourceGroupBlock key={group.kind} group={group} grid>
                      {group.items.map((source) => (
                        <SourceCard
                          key={source.n}
                          source={source}
                          projectId={projectId}
                          highlighted={highlighted === source.n}
                          onFindStep={showSteps ? findStep : undefined}
                        />
                      ))}
                    </SourceGroupBlock>
                  ))}
                </div>
              </section>
            )}
          </section>

          {/* How the answer was made, with Debug on (`architecture/0029`),
              under the sources. */}
          {answer && questionId && (
            <div className="mt-4">
              <ThreadDebug projectId={projectId} threadId={questionId} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Answer({
  message,
  onCite,
}: {
  message: ChatMessage;
  onCite: (n: number) => void;
}) {
  return (
    <div>
      {message.topic && (
        <span className="mb-1.5 inline-block font-display text-[0.62rem] font-semibold tracking-[0.08em] text-muted uppercase">
          {TOPIC_LABELS[message.topic]}
        </span>
      )}
      <AnswerBody text={message.content} sources={message.sources} onCite={onCite} />
    </div>
  );
}

/**
 * The state before anything has been asked.
 *
 * The suggestions are the questions this corpus is actually good at — and each
 * one is a question whose obvious answer is wrong, which is the point worth
 * demonstrating on the first try rather than the tenth.
 */
function Opening({
  projectName,
  onPick,
}: {
  projectName: string;
  onPick: (question: string) => void;
}) {
  const SUGGESTIONS = [
    "Is everyone on site cleared for the work they're doing?",
    "What's my exposure if we slip two weeks?",
    "Where are we against the programme?",
    "Is there anything we've promised to do and not done?",
  ];

  return (
    <div className="overflow-hidden rounded-panel border-[1.5px] border-brand bg-chrome">
      <HazardStripe height={5} />
      <div className="px-6 py-6">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="flex text-brand-bright">
            <Sparkles size={17} />
          </span>
          <span className="font-display text-[0.72rem] font-semibold tracking-[0.12em] text-brand-bright uppercase">
            Site Brain
          </span>
        </div>
        <p className="mb-5 text-[0.9rem] text-white/60">
          Every document, journal entry and crew record for {projectName} — one answer, with
          its sources.
        </p>
        <div className="flex flex-col gap-1.5">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => onPick(suggestion)}
              className={cn(
                "cursor-pointer rounded-input border border-white/12 bg-white/5 px-3.5 py-2.5",
                "text-left text-[0.87rem] text-white/80 transition-colors",
                "hover:border-brand/60 hover:bg-white/10 hover:text-white",
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
